import { EmailStatus } from '@models/portfolioadmin/reportManagement/emailStatus';
import { EmailRepo } from '@services/email.repo';

interface TableColumn {
  key: string;
  label: string;
  width?: string;
  type?: 'text' | 'date' | 'checkbox' | 'boolean' | 'currency' | 'percentage' | 'html';
}

class EmailDetailsDialogController implements angular.IOnInit {
  public email: any;
  public allRecipients: any[] = [];
  public filteredRecipients: any[] = [];
  public statusFilter: string = 'All';
  public retrying: boolean = false;
  public tableComponent: any; // Reference to reusable-table component
  public EmailStatus = EmailStatus; // Expose enum to template
  public tableColumns: TableColumn[] = [
    { key: 'email', label: 'Email Address', width: 'col-md-8' },
    { key: 'emailStatusDisplay', label: 'Status', width: 'col-md-4', type: 'html' }
  ];
  public statusOptions: any[] = [
    { value: 'All', label: 'All' },
    { value: EmailStatus.FAILED.toString(), label: 'Failed' },
    { value: EmailStatus.SENDING.toString(), label: 'Sending' },
    { value: EmailStatus.PENDING.toString(), label: 'Pending' },
    { value: EmailStatus.SENT.toString(), label: 'Sent' }
  ];

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService,
    private $window: angular.IWindowService,
    private $sce: angular.ISCEService,
    private emailRepo: EmailRepo,
    private notifyjs: any,
    private $rootScope: angular.IRootScopeService,
    private $element: angular.IAugmentedJQuery,
    private $timeout: angular.ITimeoutService
  ) {}

  $onInit(): void {
    // Initialize recipients with recipientId for selection
    if (this.email && this.email.recipients) {
      this.allRecipients = this.initializeRecipients(this.email.recipients);
      this.applyFilter();
    }

    // Get reference to reusable-table component after it's initialized
    this.$timeout(() => {
      const tableElement = this.$element.find('reusable-table');
      if (tableElement.length > 0) {
        const tableScope = tableElement.isolateScope() as any;
        if (tableScope && tableScope.$ctrl) {
          this.tableComponent = tableScope.$ctrl;
        }
      }
    }, 100);
  }

  public applyFilter(): void {
    if (this.statusFilter === 'All') {
      this.filteredRecipients = [...this.allRecipients];
    } else {
      const status = parseInt(this.statusFilter);
      this.filteredRecipients = this.allRecipients.filter((r: any) => r.emailStatus === status);
    }
    // Clear selections when filter changes
    if (this.tableComponent && this.tableComponent.deselectAll) {
      this.tableComponent.deselectAll();
    }
  }

  public deselectAll(): void {
    if (this.tableComponent && this.tableComponent.deselectAll) {
      this.tableComponent.deselectAll();
    }
  }

  public getSelectedCount(): number {
    // Access the table component's selectedItems if available
    if (this.tableComponent && this.tableComponent.selectedItems) {
      return this.tableComponent.selectedItems.size;
    }
    return 0;
  }

  public hasSelectedItems(): boolean {
    // Access the table component's selectedItems if available
    if (this.tableComponent && this.tableComponent.selectedItems) {
      return this.tableComponent.selectedItems.size > 0;
    }
    return false;
  }

  public getSelectedRecipients(): any[] {
    // Access the table component's selectedItems if available
    if (this.tableComponent && this.tableComponent.selectedItems) {
      return Array.from(this.tableComponent.selectedItems);
    }
    return [];
  }

  public retrySelected(): void {
    const selected = this.getSelectedRecipients();
    
    if (selected.length === 0) {
      this.notifyjs('Please select at least one recipient to retry.', 'warning');
      return;
    }

    const recipientIds = selected
      .map((r: any) => r.recipientId || r.id)
      .filter((id: any) => id != null);

    if (recipientIds.length === 0) {
      this.notifyjs('Selected recipients do not have valid IDs.', 'error');
      return;
    }

    this.retryEmails(recipientIds);
  }

  public retrySingleRecipient(item: any): void {
    const recipientId = item.recipientId || item.id;
    
    if (!recipientId) {
      this.notifyjs('Recipient does not have a valid ID.', 'error');
      return;
    }

    this.retryEmails([recipientId]);
  }

  private retryEmails(recipientIds: number[]): void {
    if (this.retrying) {
      return;
    }

    this.retrying = true;
    this.emailRepo.retryGroupEmail(this.email.emailId, recipientIds)
      .then(() => {
        this.notifyjs(`Retry initiated for ${recipientIds.length} recipient(s).`, 'success');
        // Refresh the email data
        this.refreshEmailData();
        // Clear selections
        this.deselectAll();
        this.retrying = false;
      })
      .catch((err) => {
        const errorMessage = (err.data && err.data.message) || err.data || err.message || 'Failed to retry emails. Please try again.';
        this.notifyjs(errorMessage, 'error');
        this.retrying = false;
      });
  }

  private refreshEmailData(): void {
    // Refresh email data from server
    this.emailRepo.getGroupEmails()
      .then((emails: any[]) => {
        const updatedEmail = emails.find((e: any) => 
          (e.EmailId || e.emailId || e.Id) === this.email.emailId
        );
        if (updatedEmail) {
          // Update recipients
          const recipients = updatedEmail.Recipients || updatedEmail.recipients || [];
          this.email.recipients = recipients.map((r: any) => ({
            recipientId: r.RecipientId || r.recipientId || r.Id || r.id,
            email: r.Email || r.email || r.emailAddress,
            emailStatus: r.EmailStatus || r.emailStatus || r.Status
          }));
          
          // Reinitialize recipients
          this.allRecipients = this.initializeRecipients(this.email.recipients);
          this.applyFilter();
        }
        // Also broadcast to refresh the main list
        this.$rootScope.$broadcast('refresh-email-list');
      })
      .catch(() => {
        // If refresh fails, just broadcast to refresh main list
        this.$rootScope.$broadcast('refresh-email-list');
      });
  }

  private initializeRecipients(recipients: any[]): any[] {
    return recipients.map((r: any, index: number) => {
      const statusDisplay = this.getStatusDisplay(r.emailStatus);
      return {
        ...r,
        id: r.recipientId || r.id || `recipient-${index}`, // Use recipientId from backend or generate unique id
        email: r.email || r.emailAddress,
        emailStatus: r.emailStatus,
        emailStatusDisplay: statusDisplay // HTML formatted status for display
      };
    });
  }

  public getTrustedHtml(html: string): any {
    if (!html) {
      return '';
    }
    return this.$sce.trustAsHtml(html);
  }

  public downloadAttachment(attachment: any): void {
    this.emailRepo.downloadAttachment(attachment.attachmentId)
      .then(res => {
        const attachmentData = res.data as any;
        this.$window.open(attachmentData.url, '_blank');
      });
  }

  public getStatusCount(status: EmailStatus): number {
    if (!this.email || !this.email.recipients) {
      return 0;
    }
    return this.email.recipients.filter((r: any) => r.emailStatus === status).length;
  }

  public getStatusBadge(status: number): any {
    switch (status) {
      case EmailStatus.SENT:
        return {
          text: 'Sent',
          class: 'badge-success',
          icon: 'check_circle',
          color: '#28a745'
        };
      case EmailStatus.SENDING:
        return {
          text: 'Sending',
          class: 'badge-warning',
          icon: 'schedule',
          color: '#ffc107'
        };
      case EmailStatus.PENDING:
        return {
          text: 'Pending',
          class: 'badge-secondary',
          icon: 'hourglass_empty',
          color: '#6c757d'
        };
      case EmailStatus.FAILED:
        return {
          text: 'Failed',
          class: 'badge-danger',
          icon: 'error',
          color: '#dc3545'
        };
      default:
        return {
          text: 'Unknown',
          class: 'badge-secondary',
          icon: 'help',
          color: '#6c757d'
        };
    }
  }

  public getStatusDisplay(status: number): string {
    const badge = this.getStatusBadge(status);
    return `<span style="color: ${badge.color}; font-weight: 500;">${badge.text}</span>`;
  }

  public cancel(): void {
    this.$mdDialog.cancel();
  }
}

export const emailDetailsDialog: angular.IComponentOptions = {
  controller: EmailDetailsDialogController,
  template: require('./emailDetails.dialog.html'),
  bindings: {
    email: '<'
  }
};




// WEBPACK FOOTER //
// ./src/app/bulkemailing/components/emailDetails.dialog.ts